import React, { useState, useEffect, useCallback } from "react";
import { useMap, GeoJSON } from "react-leaflet";
import * as turf from "@turf/turf";
import { useDispatch, useSelector } from "react-redux";
import { setMapBounds } from "../../../features/maps/mapZoomSlice";
import { selectLayerDataSet } from "../../../features/layers/overlaylayerslice";
import styled from "styled-components";
import { FaThumbtack, FaRuler, FaDrawPolygon, FaTimes } from 'react-icons/fa';
import { closeTools } from "../../../features/tools/toolsSlice";

const Loader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  border-radius: 8px;

  .spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1890ff;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const NotificationModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <ModalWrapper type={type}>
      <div className="modal-content">
        <div className="modal-message">{message}</div>
        <button className="modal-close" onClick={onClose}>
          <FaTimes />
        </button>
      </div>
    </ModalWrapper>
  );
};

const ProxyToolsEdit = () => {
  const map = useMap();
  const dispatch = useDispatch();
  const overlayLayers = useSelector(selectLayerDataSet);

  const [buff, setBuff] = useState(10);
  const [aoi, setAoi] = useState({ id: null, geojson: null });
  const [poi, setPoi] = useState(null);
  const [boundingbox, setBoundingBox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainData, setMainData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  useEffect(() => {
    return () => {
      map.eachLayer((layer) => {
        if (layer.pm != null) layer.remove();
      });
    };
  }, [map]);

  useEffect(() => {
    const selectedData = overlayLayers.find(
      (data) => data.text === "Mithuna GLB" && data.class === "Model" && data.show
    );
    setMainData(selectedData || null);
  }, [overlayLayers]);

  useEffect(() => {
    if (poi !== null) {
      const buffered = turf.buffer(poi, buff, { units: "miles" });
      const bbox = turf.bbox(buffered);
      setBoundingBox(bbox);
      setAoi({ id: buff, geojson: buffered });
    }
  }, [buff, poi]);

  const handleDrawCreate = useCallback((e) => {
    if (e.shape === "Marker") {
      const point = turf.point([e.layer._latlng.lng, e.layer._latlng.lat]);
      setPoi(point);
      const buffered = turf.buffer(point, buff, { units: "miles" });
      setAoi({ id: e.layer._leaflet_id, geojson: buffered });
    } else if (e.shape === "Polygon") {
      const ccds1 = e.layer._latlngs[0].map((e) => [e.lng, e.lat]);
      ccds1.push(ccds1[0]);
      const polygon = turf.polygon([ccds1]);
      setPoi(polygon);
      const buffered = turf.buffer(polygon, buff, { units: "miles" });
      setAoi({ id: e.layer._leaflet_id, geojson: buffered });
    } else if (e.shape === "Line") {
      const ccds1 = e.layer._latlngs.map((e) => [e.lng, e.lat]);
      const line = turf.lineString(ccds1);
      setPoi(line);
      const buffered = turf.buffer(line, buff, { units: "miles" });
      setAoi({ id: e.layer._leaflet_id, geojson: buffered });
    }
    map.pm.disableDraw();
  }, [buff, map]);

  useEffect(() => {
    map.on("pm:create", handleDrawCreate);
    return () => {
      map.off("pm:create", handleDrawCreate);
    };
  }, [handleDrawCreate, map]);

  const togglePoint = useCallback(() => {
    clearDrawingLayers();
    map.pm.enableDraw("Marker");
  }, [map]);

  const togglePolygon = useCallback(() => {
    clearDrawingLayers();
    map.pm.enableDraw("Polygon");
  }, [map]);

  const togglePolyline = useCallback(() => {
    clearDrawingLayers();
    map.pm.enableDraw("Line");
  }, [map]);

  const clearDrawingLayers = useCallback(() => {
    map.eachLayer((layer) => {
      if (layer.pm != null) layer.remove();
    });
    setPoi(null);
    setAoi({ id: null, geojson: null });
    setBoundingBox(null);
  }, [map]);

  const ChangeMap = useCallback(() => {
    dispatch(closeTools({ name: "Proximity" }));
  }, [dispatch]);

  const mainSubmit = useCallback(async () => {
    if (!boundingbox) {
      showNotification("Draw an area");
      return;
    }
    if (!mainData) {
      showNotification("Select a model or select a layer first");
      return;
    }

    const bbox = boundingbox;
    const apiUrl = `https://api.ncmrwf.gov.in/${mainData.datatype.toLowerCase()}Stats/?date=${mainData.date}&odate=${mainData.odate}&pressure=925&hour=${mainData.hours}&bbox=${bbox.join(',')}`;

    setLoading(true);

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Fetch failed");
      const data = await response.json();
      const mdata = { [mainData.datatype.toLowerCase()]: data };
      dispatch(setMapBounds({ geojson: data, url: apiUrl, extra: mdata, kela: 'yes' }));
      showNotification("Data fetched successfully", "success");
    } catch (err) {
      console.error(err);
      showNotification("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [boundingbox, mainData, dispatch]);

  const handleSliderStart = useCallback(() => {
    map.dragging.disable();
  }, [map]);

  const handleSliderEnd = useCallback(() => {
    map.dragging.enable();
  }, [map]);

  const handleSliderChange = useCallback((e) => {
    setBuff(parseFloat(e.target.value));
  }, []);

  return (
    <Filters isMobile={isMobile}>
      <div 
        id="ediit" 
        className="Editable Leaflet-Control"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div onClick={ChangeMap} className="close-button">
          <FaTimes className="Close" />
        </div>

        <div className="Editable-Tools">
          <button className="control-button" onClick={togglePoint} title="Add Point">
            <FaThumbtack />
            {!isMobile && <span>Point</span>}
          </button>
          <button className="control-button" onClick={togglePolyline} title="Add Line">
            <FaRuler />
            {!isMobile && <span>Line</span>}
          </button>
          <button className="control-button" onClick={togglePolygon} title="Add Polygon">
            <FaDrawPolygon />
            {!isMobile && <span>Polygon</span>}
          </button>
        </div>

        <div className="buffer-controls">
          <label htmlFor="bufferRange">Buffer: {buff} miles</label>
          <input
            id="bufferRange"
            type="range"
            min={10}
            step={2}
            max={50}
            value={buff}
            onChange={handleSliderChange}
            onMouseDown={handleSliderStart}
            onMouseUp={handleSliderEnd}
            onTouchStart={handleSliderStart}
            onTouchEnd={handleSliderEnd}
          />
        </div>

        <button className="submit-button" onClick={mainSubmit} disabled={loading}>
          {loading ? "Processing..." : "Compute"}
        </button>
      </div>

      {loading && <Loader />}
      {notification && (
        <NotificationModal
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
      {aoi.geojson && <GeoJSON key={aoi.id} data={aoi.geojson} />}
    </Filters>
  );
};

export default ProxyToolsEdit;

const Filters = styled.div`
  position: absolute;
  left: ${props => props.isMobile ? '10px' : '50%'};
  ${props => !props.isMobile && 'transform: translateX(-50%);'}
  bottom: ${props => props.isMobile ? '120px' : '20px'};
  background: rgba(3, 57, 105, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  font-family: 'Inter-Regular', sans-serif;
  padding: ${props => props.isMobile ? '10px' : '15px'};
  width: ${props => props.isMobile ? 'calc(100% - 20px)' : 'auto'};
  max-width: 95%;
  z-index: 1000;
  box-sizing: border-box;

  .Editable {
    width: 100%;
    box-sizing: border-box;
    pointer-events: auto;
  }

  .Editable-Tools {
    display: flex;
    justify-content: space-around;
    margin: 15px 0;
    flex-wrap: wrap;
    gap: ${props => props.isMobile ? '5px' : '10px'};
  }

  .control-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    font-size: ${props => props.isMobile ? '14px' : '16px'};
    color: #555;
    cursor: pointer;
    transition: all 0.2s;
    padding: ${props => props.isMobile ? '8px' : '10px'};
    border: 1px solid #ccc;
    border-radius: 5px;
    background: white;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    min-width: ${props => props.isMobile ? 'auto' : '80px'};
    height: ${props => props.isMobile ? '36px' : '40px'};
    white-space: nowrap;

    &:hover {
      color: #1890ff;
      transform: scale(1.05);
    }

    svg {
      flex-shrink: 0;
    }
  }

  .buffer-controls {
    margin: 15px 0;
    pointer-events: auto;
    touch-action: none;

    label {
      display: block;
      font-size: ${props => props.isMobile ? '12px' : '14px'};
      color: white;
      margin-bottom: 5px;
    }

    input[type="range"] {
      width: 100%;
      margin-bottom: 5px;
      -webkit-appearance: none;
      height: 5px;
      background: #ddd;
      border-radius: 5px;
      outline: none;
      touch-action: none;

      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        background: #1890ff;
        border-radius: 50%;
        cursor: pointer;
        touch-action: none;
      }
    }
  }

  .submit-button {
    width: 100%;
    padding: ${props => props.isMobile ? '8px' : '10px'};
    background: #1890ff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
    font-size: ${props => props.isMobile ? '13px' : '14px'};
    font-weight: 500;

    &:hover {
      background: #40a9ff;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }

  .close-button {
    position: absolute;
    right: 10px;
    top: 10px;
    cursor: pointer;
    color: white;
    font-size: 16px;
    background: rgba(0,0,0,0.2);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #fff;
      background: rgba(0,0,0,0.3);
    }
  }
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${props => props.type === 'success' ? '#4CAF50' : '#f44336'};
  color: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  max-width: 300px;
  animation: slideIn 0.3s ease-out;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
    padding: 10px;
    font-size: 14px;
  }

  .modal-content {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .modal-message {
    flex-grow: 1;
    margin-right: 10px;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;